import gsap from "gsap"
import { activeMultiplier } from "./animationSpeed"
import { playStrikeBeamSound } from "./audioManager"

/**
 * A cinematic "striking beam" that snaps from a source point (the played card,
 * resting at the top-center of the screen) toward a targeted player's panel and
 * detonates on impact. The beam is a jagged, flickering lightning bolt so it
 * reads as a *strike* rather than a smooth laser. Used for cards that act
 * against a specific player such as "apply property tax" and "perform tax
 * audit".
 *
 * The whole effect is rendered into a throwaway, pointer-events-none overlay
 * appended to <body> and driven by a single GSAP timeline, then cleaned up — so
 * it never interferes with React's render tree or the underlying player panels.
 */

// Danger/energy palette: white-hot core bleeding into amber and crimson.
const CORE = "#fff6e6"
const AMBER = "#ff9d2f"
const CRIMSON = "#ff3b30"
const SVG_NS = "http://www.w3.org/2000/svg"

interface IStrikingBeamOptions {
  /** Order of the player the beam should hit. */
  toOrder?: PlayerOrder
  /**
   * Order of the board property (cell) the beam should hit. Takes priority over
   * `toOrder` when provided — used by forced auction/buy/downgrade and
   * purchase-at-double-price.
   */
  toPropertyOrder?: number
  /**
   * Optional explicit origin in viewport coordinates. Defaults to the resting
   * position of the played card (top-center), matching `playActionCard`.
   */
  fromPoint?: { x: number; y: number }
  /**
   * Whether this beam plays the strike sound. Defaults to true; set false for
   * the follow-up beams of a multi-target fan-out so the zaps don't stack.
   */
  playSound?: boolean
}

const makeEl = (parent: HTMLElement, styles: Partial<CSSStyleDeclaration>): HTMLDivElement => {
  const el = document.createElement("div")
  el.style.position = "absolute"
  el.style.pointerEvents = "none"
  el.style.willChange = "transform, opacity"
  Object.assign(el.style, styles)
  parent.appendChild(el)
  return el
}

/**
 * Build the `d` for a jagged lightning bolt between two points: the straight
 * line is broken into segments whose interior vertices are jittered
 * perpendicular to the travel direction (tapered to zero at both ends so it
 * stays anchored on the card and the target).
 */
const buildBolt = (
  from: { x: number; y: number },
  to: { x: number; y: number },
  segments: number,
  amplitude: number
): string => {
  const dx = to.x - from.x
  const dy = to.y - from.y
  const len = Math.hypot(dx, dy) || 1
  const nx = -dy / len
  const ny = dx / len
  let d = `M ${from.x.toFixed(1)} ${from.y.toFixed(1)}`
  for (let i = 1; i <= segments; i++) {
    const t = i / segments
    const taper = Math.sin(Math.PI * t) // 0 at ends, 1 in the middle
    const off = i === segments ? 0 : (Math.random() * 2 - 1) * amplitude * taper
    const x = from.x + dx * t + nx * off
    const y = from.y + dy * t + ny * off
    d += ` L ${x.toFixed(1)} ${y.toFixed(1)}`
  }
  return d
}

/**
 * A short, decaying shake + scale-punch (with a kick of rotation) applied
 * directly to the targeted player's panel to sell the moment of impact. The
 * panel carries a CSS `transition-all`, which would smear a transform tween —
 * so we disable the inline transition for the shake and restore it (and clear
 * the transform) once it settles.
 */
const shakeTarget = (element: HTMLElement) => {
  const prevTransition = element.style.transition
  gsap.killTweensOf(element)
  element.style.transition = "none"
  gsap.to(element, {
    keyframes: [
      { x: -16, y: 9, rotation: -2.5, scale: 1.07, duration: 0.05 },
      { x: 14, y: -11, rotation: 2.2, scale: 1.06, duration: 0.05 },
      { x: -11, y: 8, rotation: -1.7, scale: 1.045, duration: 0.05 },
      { x: 9, y: -6, rotation: 1.2, scale: 1.03, duration: 0.05 },
      { x: -6, y: 4, rotation: -0.7, scale: 1.02, duration: 0.05 },
      { x: 4, y: -2, rotation: 0.3, scale: 1.01, duration: 0.05 },
      { x: -2, y: 1, rotation: 0, scale: 1.0, duration: 0.05 },
      { x: 0, y: 0, rotation: 0, scale: 1, duration: 0.06 },
    ],
    ease: "none",
    onComplete: () => {
      gsap.set(element, { clearProps: "transform" })
      element.style.transition = prevTransition
    },
  })
}

export const runStrikingBeamAnimation = ({
  toOrder,
  toPropertyOrder,
  fromPoint,
  playSound = true,
}: IStrikingBeamOptions): Promise<void> => {
  return new Promise((resolve) => {
    const isProperty = toPropertyOrder != null
    const selector = isProperty ? `#cell-${toPropertyOrder}` : `#player-list-item-${toOrder}`
    const targetEl = document.querySelector(selector) as HTMLElement | null
    // Nothing to strike (target not mounted / bankrupt panel gone) — bail quietly.
    if (!targetEl) {
      resolve()
      return
    }

    const rect = targetEl.getBoundingClientRect()
    if (!rect.width || !rect.height) {
      resolve()
      return
    }

    const to = { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 }
    const from = fromPoint ?? { x: window.innerWidth / 2, y: window.innerHeight / 4 }

    const dx = to.x - from.x
    const dy = to.y - from.y
    const length = Math.hypot(dx, dy) || 1

    const mult = activeMultiplier()

    // Full-screen overlay that hosts every piece of the effect.
    const container = document.createElement("div")
    Object.assign(container.style, {
      position: "fixed",
      inset: "0",
      zIndex: "130",
      pointerEvents: "none",
      overflow: "hidden",
    } as Partial<CSSStyleDeclaration>)
    document.body.appendChild(container)

    let settled = false
    const cleanup = () => {
      if (settled) return
      settled = true
      container.remove()
      resolve()
    }

    // A "hit" ring hugging the targeted player's panel — the shared impact cue
    // for both the full and the reduced-motion path.
    const pad = 6
    const halo = makeEl(container, {
      left: `${rect.x - pad}px`,
      top: `${rect.y - pad}px`,
      width: `${rect.width + pad * 2}px`,
      height: `${rect.height + pad * 2}px`,
      // Hug the square board cell more tightly than the rounded player panel.
      borderRadius: isProperty ? "8px" : "14px",
      border: `2px solid ${AMBER}`,
      boxShadow: `0 0 18px 4px rgba(255,157,47,0.75), inset 0 0 22px 2px rgba(255,59,48,0.55)`,
      opacity: "0",
    })

    // Respect reduced-motion: skip the beam choreography, just flash the target.
    const prefersReducedMotion =
      typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    if (prefersReducedMotion) {
      if (playSound) playStrikeBeamSound()
      gsap
        .timeline({ onComplete: cleanup })
        .fromTo(halo, { opacity: 0, scale: 1.08 }, { opacity: 1, scale: 1, duration: 0.12 })
        .to(halo, { opacity: 0, duration: 0.35, delay: 0.15 })
      return
    }

    // --- Lightning bolt (SVG) -----------------------------------------------

    const segments = Math.min(Math.max(Math.round(length / 55), 5), 9)
    const amplitude = Math.min(length * 0.09, 26)

    const svg = document.createElementNS(SVG_NS, "svg")
    svg.setAttribute("width", "100%")
    svg.setAttribute("height", "100%")
    Object.assign(svg.style, {
      position: "absolute",
      inset: "0",
      overflow: "visible",
      pointerEvents: "none",
    } as Partial<CSSStyleDeclaration>)
    const boltGroup = document.createElementNS(SVG_NS, "g")
    svg.appendChild(boltGroup)
    container.appendChild(svg)

    const makePath = (width: number, stroke: string, filter: string, opacity = "1"): SVGPathElement => {
      const p = document.createElementNS(SVG_NS, "path")
      p.setAttribute("fill", "none")
      p.setAttribute("stroke", stroke)
      p.setAttribute("stroke-width", String(width))
      p.setAttribute("stroke-linecap", "round")
      p.setAttribute("stroke-linejoin", "round")
      p.style.filter = filter
      p.style.opacity = opacity
      boltGroup.appendChild(p)
      return p
    }

    // Layered strokes: a wide blurred halo, an amber body, a white-hot core.
    const boltHalo = makePath(13, AMBER, "blur(6px)", "0.6")
    const boltBody = makePath(5, "#ffb057", `drop-shadow(0 0 6px ${CRIMSON})`)
    const boltCore = makePath(2.4, "#ffffff", `drop-shadow(0 0 5px ${AMBER}) drop-shadow(0 0 10px ${CRIMSON})`)
    const boltPaths = [boltHalo, boltBody, boltCore]

    const initialD = buildBolt(from, to, segments, amplitude)
    boltPaths.forEach((p) => p.setAttribute("d", initialD))

    // Prime the "draw" so the bolt snaps in from the card to the target.
    const boltLen = boltCore.getTotalLength() || length
    boltPaths.forEach((p) => {
      p.style.strokeDasharray = String(boltLen)
      p.style.strokeDashoffset = String(boltLen)
    })

    // --- Point elements ------------------------------------------------------

    // Muzzle charge/flash at the card.
    const charge = makeEl(container, {
      left: `${from.x}px`,
      top: `${from.y}px`,
      width: "48px",
      height: "48px",
      borderRadius: "50%",
      background: `radial-gradient(circle, #ffffff 0%, ${CORE} 25%, ${AMBER} 55%, rgba(255,59,48,0) 75%)`,
      boxShadow: `0 0 24px 7px rgba(255,157,47,0.9)`,
      opacity: "0",
    })
    gsap.set(charge, { xPercent: -50, yPercent: -50, scale: 0.2 })

    // A brief full-scene flash — the lightning "lighting up the room" on impact.
    const sceneFlash = makeEl(container, {
      inset: "0",
      background: `radial-gradient(circle at ${to.x}px ${to.y}px, rgba(255,220,170,0.55) 0%, rgba(255,150,80,0.18) 25%, rgba(255,120,60,0) 55%)`,
      opacity: "0",
    })

    // Impact flash + shockwave rings at the target.
    const flash = makeEl(container, {
      left: `${to.x}px`,
      top: `${to.y}px`,
      width: "160px",
      height: "160px",
      borderRadius: "50%",
      background: `radial-gradient(circle, #ffffff 0%, ${AMBER} 35%, rgba(255,59,48,0) 70%)`,
      opacity: "0",
    })
    gsap.set(flash, { xPercent: -50, yPercent: -50, scale: 0.3 })

    const flashCore = makeEl(container, {
      left: `${to.x}px`,
      top: `${to.y}px`,
      width: "84px",
      height: "84px",
      borderRadius: "50%",
      background: `radial-gradient(circle, #ffffff 0%, ${CORE} 45%, rgba(255,157,47,0) 72%)`,
      opacity: "0",
    })
    gsap.set(flashCore, { xPercent: -50, yPercent: -50, scale: 0.2 })

    const ring = makeEl(container, {
      left: `${to.x}px`,
      top: `${to.y}px`,
      width: "60px",
      height: "60px",
      borderRadius: "50%",
      border: `5px solid ${AMBER}`,
      boxShadow: `0 0 18px 3px rgba(255,157,47,0.85)`,
      opacity: "0",
    })
    gsap.set(ring, { xPercent: -50, yPercent: -50, scale: 0.4 })

    const ring2 = makeEl(container, {
      left: `${to.x}px`,
      top: `${to.y}px`,
      width: "50px",
      height: "50px",
      borderRadius: "50%",
      border: `3px solid ${CRIMSON}`,
      opacity: "0",
    })
    gsap.set(ring2, { xPercent: -50, yPercent: -50, scale: 0.3 })

    // Sharp impact rays ("hit star") that snap outward on contact.
    const rayCount = 9
    const rays = Array.from({ length: rayCount }).map((_, i) => {
      const ray = makeEl(container, {
        left: `${to.x}px`,
        top: `${to.y}px`,
        width: "4px",
        height: "3px",
        borderRadius: "999px",
        background: CORE,
        boxShadow: `0 0 8px 1px rgba(255,157,47,0.95)`,
        opacity: "0",
      })
      gsap.set(ray, {
        rotation: (360 / rayCount) * i + gsap.utils.random(-12, 12),
        yPercent: -50,
        transformOrigin: "0% 50%",
      })
      return ray
    })

    // Radial spark burst on impact.
    const sparkCount = 16
    const sparks = Array.from({ length: sparkCount }).map((_, i) => {
      const spark = makeEl(container, {
        left: `${to.x}px`,
        top: `${to.y}px`,
        width: "7px",
        height: "7px",
        borderRadius: "50%",
        background: i % 2 === 0 ? CORE : AMBER,
        boxShadow: `0 0 8px 2px rgba(255,157,47,0.9)`,
        opacity: "0",
      })
      gsap.set(spark, { xPercent: -50, yPercent: -50 })
      return spark
    })

    // Property strikes must NOT transform the real board cell: it lives in a CSS
    // grid and is rotated per board side, so GSAP-ing its transform would spin
    // it out of place. A glowing overlay box sitting over the cell takes the
    // jolt instead, leaving the board untouched.
    const cellPunch = isProperty
      ? makeEl(container, {
          left: `${rect.x - 2}px`,
          top: `${rect.y - 2}px`,
          width: `${rect.width + 4}px`,
          height: `${rect.height + 4}px`,
          borderRadius: "8px",
          border: `3px solid ${CORE}`,
          boxShadow: `0 0 22px 5px rgba(255,157,47,0.85), inset 0 0 26px 6px rgba(255,157,47,0.4)`,
          opacity: "0",
        })
      : null
    if (cellPunch) gsap.set(cellPunch, { transformOrigin: "50% 50%", scale: 0.8 })

    // --- Timeline ------------------------------------------------------------

    const tl = gsap.timeline({ onComplete: cleanup })

    // Charge up at the muzzle (quick — the strike wants to feel eager).
    tl.to(charge, {
      opacity: 1,
      scale: 1.35,
      duration: 0.12 * mult,
      ease: "back.out(2.2)",
      onStart: () => {
        if (playSound) playStrikeBeamSound()
      },
    })

    const fireAt = tl.time()
    const drawDur = 0.08 * mult
    const impactAt = fireAt + drawDur

    // Strike: the bolt snaps from the card to the target very fast.
    const drawProxy = { p: 0 }
    tl.to(
      drawProxy,
      {
        p: 1,
        duration: drawDur,
        ease: "power2.out",
        onUpdate: () => {
          const off = boltLen * (1 - drawProxy.p)
          boltPaths.forEach((p) => (p.style.strokeDashoffset = String(off)))
        },
        onComplete: () => {
          // Full bolt now shown — drop the dash so a re-jag can't clip it.
          boltPaths.forEach((p) => {
            p.style.strokeDasharray = "none"
            p.style.strokeDashoffset = "0"
          })
        },
      },
      fireAt
    )
      // Muzzle burst as it launches.
      .to(charge, { scale: 0.5, opacity: 0, duration: 0.16 * mult, ease: "power2.in" }, fireAt)

    // Electric flicker + a mid-strike "re-jag" so the bolt crackles.
    tl.to(
      boltGroup,
      {
        keyframes: [
          { opacity: 0.35, duration: 0.03 },
          { opacity: 1, duration: 0.03 },
          { opacity: 0.6, duration: 0.03 },
          { opacity: 1, duration: 0.04 },
        ],
        ease: "none",
      },
      impactAt
    ).call(
      () => {
        const reJag = buildBolt(from, to, segments, amplitude)
        boltPaths.forEach((p) => p.setAttribute("d", reJag))
      },
      [],
      impactAt + 0.05 * mult
    )

    // Bolt dissipates shortly after it lands.
    tl.to(boltGroup, { opacity: 0, duration: 0.2 * mult, ease: "power2.in" }, impactAt + 0.16 * mult)

    // Scene-wide flash — the room lights up for a frame.
    tl.set(sceneFlash, { opacity: 1 }, impactAt).to(
      sceneFlash,
      { opacity: 0, duration: 0.24 * mult, ease: "power2.out" },
      impactAt
    )

    // Core impact: flash, flash-pop, and the two shockwaves.
    tl.set(flash, { opacity: 1 }, impactAt)
      .to(flash, { scale: 2.1, opacity: 0, duration: 0.36 * mult, ease: "power2.out" }, impactAt)
      .set(flashCore, { opacity: 1 }, impactAt)
      .to(flashCore, { scale: 1.9, opacity: 0, duration: 0.16 * mult, ease: "power2.out" }, impactAt)
      .set(ring, { opacity: 0.98 }, impactAt)
      .to(ring, { scale: 4.2, opacity: 0, duration: 0.46 * mult, ease: "power2.out" }, impactAt)
      .set(ring2, { opacity: 0.88 }, impactAt + 0.08 * mult)
      .to(ring2, { scale: 3.4, opacity: 0, duration: 0.42 * mult, ease: "power2.out" }, impactAt + 0.08 * mult)
      .fromTo(
        halo,
        { opacity: 0, scale: 1.08 },
        { opacity: 1, scale: 1, duration: 0.1 * mult, ease: "power2.out" },
        impactAt
      )
      .to(halo, { opacity: 0, duration: 0.42 * mult, ease: "power2.in" }, impactAt + 0.18 * mult)

    // Impact rays snap outward, then vanish.
    rays.forEach((ray) => {
      const rayLen = 38 + Math.random() * 30
      tl.set(ray, { opacity: 1, width: 4 }, impactAt)
        .to(ray, { width: rayLen, duration: 0.12 * mult, ease: "power3.out" }, impactAt)
        .to(ray, { opacity: 0, duration: 0.14 * mult, ease: "power1.in" }, impactAt + 0.1 * mult)
    })

    // Kick the struck target — the core "it got hit" cue. Player panels take a
    // direct shake; properties jolt the overlay box (never the real cell).
    if (cellPunch) {
      tl.to(
        cellPunch,
        {
          keyframes: [
            { opacity: 1, scale: 1.14, rotation: -3, duration: 0.06 },
            { scale: 1.06, rotation: 2.2, duration: 0.05 },
            { scale: 1.11, rotation: -1.4, duration: 0.05 },
            { scale: 1.02, rotation: 0.8, duration: 0.05 },
            { scale: 1.05, rotation: 0, duration: 0.05 },
            { scale: 1.0, opacity: 0.9, duration: 0.06 },
            { scale: 1.0, opacity: 0, duration: 0.24 },
          ],
          ease: "power2.out",
        },
        impactAt
      )
    } else {
      tl.call(() => shakeTarget(targetEl), [], impactAt)
    }

    // Spark burst.
    sparks.forEach((spark, i) => {
      const a = (Math.PI * 2 * i) / sparkCount + Math.random() * 0.4
      const r = 65 + Math.random() * 95
      tl.set(spark, { opacity: 1, x: 0, y: 0, scale: 1 }, impactAt)
        .to(
          spark,
          {
            x: Math.cos(a) * r,
            y: Math.sin(a) * r + 22, // slight downward drift
            scale: 0,
            opacity: 0,
            duration: (0.42 + Math.random() * 0.22) * mult,
            ease: "power2.out",
          },
          impactAt
        )
    })

    // Safety net: force cleanup even if GSAP is throttled/tab-backgrounded.
    window.setTimeout(cleanup, 1600 * mult + 400)
  })
}
